import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { aLogEvent, AMPLITUDE_EVENT } from '@app/shared/amplitude-events';
import { EventImpl } from '@fullcalendar/core/internal';
import { Select, Store } from '@ngxs/store';
import { SchedulerCoreState, SchedulerTrack } from '@radioking/scheduler-core';
import { MobileDatePickerDialogComponent } from 'libs/ui/mobile-date-picker-dialog/mobile-date-picker-dialog.component';
import { Observable } from 'rxjs';
import { ProgramsState } from '../../../../../programs/src/lib/states/programs.state';

import {
  MOBILE_CALENDAR_DIALOG_HEIGHT,
  MOBILE_DIALOG_CONFIG,
} from '@app/shared/constants';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { filter } from 'rxjs/operators';
import { CalendarDatesChanged } from '../../models/break.model';
import { DateSelectDatesChanged } from '../../models/date-select.model';
import { SetCurrentDay } from '../../states/break.actions';
import { BreakState } from '../../states/break.state';
import { BreakDeletionComponent } from '../break-deletion/break-deletion.component';

@Component({
  selector: 'rk-break-header',
  templateUrl: './break-header.component.html',
  styleUrls: ['./break-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreakHeaderComponent {
  @Select(BreakState.currentDay)
  currentDay$: Observable<moment.Moment>;

  @Input()
  calendarDatesChanged: CalendarDatesChanged;

  @Output()
  readonly calendarTab = new EventEmitter<void>();

  @Output()
  readonly addSlot = new EventEmitter<void>();

  @Output()
  readonly addBreak = new EventEmitter<void>();

  @Output()
  readonly dateSelectDatesChanged = new EventEmitter<DateSelectDatesChanged>();

  @Output()
  readonly calendarPrevious = new EventEmitter<void>();

  @Output()
  readonly calendarNext = new EventEmitter<void>();

  /**
   * Set planning to current day/week
   */
  @Output()
  readonly calendarToday = new EventEmitter<void>();

  @Select(SchedulerCoreState.hasNotEmptyPlaylists)
  hasPlaylists$: Observable<boolean>;
  @Select(ProgramsState.hasNotEmptyPrograms)
  hasPrograms$: Observable<boolean>;
  @Select(BreakState.selectedBreaksCount)
  selectedBreaks$: Observable<EventImpl[]>;
  @Select(SchedulerCoreState.allTracks)
  tracks$: Observable<SchedulerTrack[]>;

  constructor(
    private readonly dialog: MatDialog,
    private readonly store: Store,
  ) {}

  /**
   * "Add break" button clicked
   */
  addBreakClicked(): void {
    aLogEvent(AMPLITUDE_EVENT.BREAKS.CTA_ADD);
    this.addBreak.next();
  }

  /**
   * Selected date changed
   */
  dateSelectDatesUpdated(event: DateSelectDatesChanged): void {
    this.dateSelectDatesChanged.emit(event);
  }

  /**
   * "Previous" button clicked
   */
  calendarPreviousClicked(): void {
    this.calendarPrevious.next();
  }

  /**
   * "Next" button clicked
   */
  calendarNextClicked(): void {
    this.calendarNext.next();
  }

  /**
   * "Today" button clicked
   */
  calendarTodayClicked(): void {
    this.calendarToday.next();
  }

  deleteBreaks() {
    this.dialog.open(BreakDeletionComponent, { data: null });
  }

  selectDate() {
    const dialog = this.dialog.open(MobileDatePickerDialogComponent, {
      ...MOBILE_DIALOG_CONFIG,
      height: MOBILE_CALENDAR_DIALOG_HEIGHT,
      data: { date: this.store.selectSnapshot(BreakState.currentDay) },
    });
    dialog
      .afterClosed()
      .pipe(filter(date => !!date))
      .subscribe((selectedDate: moment.Moment) => {
        this.setCurrentDay(selectedDate);
      });
  }

  @Dispatch()
  setCurrentDay = (selectedDate: moment.Moment) => new SetCurrentDay(selectedDate);
}
